"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import NextImage from "next/image";
import { getImageDimensions } from "@/resources/image-dimensions";
import styles from "./Lightbox.module.scss";

/* Matches the exit keyframes in Lightbox.module.scss (--oi-dur-exit + a
   frame of slack so the last paint is the empty backdrop). */
const EXIT_MS = 170;

interface LightboxProps {
  images: string[];
  initialIndex: number;
  title: string;
  onClose: () => void;
}

export const Lightbox: React.FC<LightboxProps> = ({
  images,
  initialIndex,
  title,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isScrollable, setIsScrollable] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [closing, setClosing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef(0);

  /* Every close path plays the exit (backdrop and image leave together,
     accelerating, faster than they arrived) before unmounting. Reduced
     motion collapses the CSS, so it unmounts at once. */
  const close = useCallback(() => {
    if (closeTimer.current) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduce.matches) {
      onClose();
      return;
    }
    setClosing(true);
    closeTimer.current = window.setTimeout(onClose, EXIT_MS);
  }, [onClose]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const prev = useCallback(
    () => setCurrentIndex((i) => Math.max(i - 1, 0)),
    [],
  );
  const next = useCallback(
    () => setCurrentIndex((i) => Math.min(i + 1, images.length - 1)),
    [images.length],
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [close, next, prev]);

  // Reset scroll and scrollable state when image changes
  useEffect(() => {
    setIsScrollable(false);
    setHasScrolled(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [currentIndex]);

  const checkScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setIsScrollable(el.scrollHeight > el.clientHeight + 8);
    }
  }, []);

  const handleScroll = () => {
    if (scrollRef.current && scrollRef.current.scrollTop > 10) {
      setHasScrolled(true);
    }
  };

  const dims = getImageDimensions(images[currentIndex]);

  return (
    <div
      className={styles.overlay}
      data-closing={closing || undefined}
      onClick={close}
    >
      <div className={styles.closeGroup}>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M1 1l10 10M11 1L1 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.escHint}>ESC</span>
      </div>

      <div ref={scrollRef} className={styles.scroll} onScroll={handleScroll}>
        <div className={styles.frame}>
          <NextImage
            key={images[currentIndex]}
            src={images[currentIndex]}
            alt={`${title} (${currentIndex + 1})`}
            width={dims.width}
            height={dims.height}
            sizes="(max-width: 1280px) 92vw, 1280px"
            quality={90}
            priority
            className={styles.image}
            onLoad={checkScrollable}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </div>
      </div>

      {isScrollable && !hasScrolled && (
        <>
          <div className={styles.scrollFade} />
          <div className={styles.scrollHint}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 3v10M4 9l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Scroll
          </div>
        </>
      )}

      {images.length > 1 && (
        <div className={styles.pager} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={styles.pagerButton}
            aria-label="Previous image"
            onClick={prev}
            disabled={currentIndex === 0}
          >
            ←
          </button>
          <span className={styles.counter}>
            {currentIndex + 1} / {images.length}
          </span>
          <button
            type="button"
            className={styles.pagerButton}
            aria-label="Next image"
            onClick={next}
            disabled={currentIndex === images.length - 1}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};
